import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../store/useAuthStore';
import { getOfflineAccountSnapshot, saveIsEmployeeOffline } from '../../shell/session/offlineAccountCache';
import { isDatabaseUnavailableError } from '../../shared/localDb';
import { recordStartupNetworkCall } from '../../shared/dev/networkCallAudit';
import { SESSION_CACHE_MS } from '../../shell/session/sessionCacheTtl';
import type { SessionEmployeeRecord } from './sessionEmployeeTypes';
export type { SessionEmployeeRecord } from './sessionEmployeeTypes';

let sessionEmployeeInFlight: Promise<SessionEmployeeRecord> | null = null;
let sessionEmployeeCache: { value: SessionEmployeeRecord; fetchedAt: number; userId: string } | null = null;

export function clearSessionEmployeeCache(): void {
  sessionEmployeeInFlight = null;
  sessionEmployeeCache = null;
}

export function primeSessionEmployeeCache(userId: string, value: SessionEmployeeRecord): void {
  sessionEmployeeCache = { value, fetchedAt: Date.now(), userId };
}

/**
 * role='owner' es la membresía del dueño (identidad para agenda/atribución),
 * no empleo — nunca debe contar como employee row para la sesión.
 */
export function isSessionEmployeeRow(row: { role?: string | null }): boolean {
  return row.role !== 'owner';
}

/**
 * Una sola consulta `employees` por sesión (id + is_active + stamp) con cache e in-flight compartidos.
 * Incluye filas inactivas (revocadas) para soft-block en AppNavigator.
 */
export async function resolveSessionEmployee(
  userId?: string,
  options?: { force?: boolean }
): Promise<SessionEmployeeRecord> {
  const uid = userId ?? useAuthStore.getState().session?.user?.id;
  if (!uid) {
    return { employeeId: null, isEmployee: false, isActive: true };
  }

  const now = Date.now();
  if (
    !options?.force &&
    sessionEmployeeCache &&
    sessionEmployeeCache.userId === uid &&
    now - sessionEmployeeCache.fetchedAt < SESSION_CACHE_MS
  ) {
    return sessionEmployeeCache.value;
  }

  if (!options?.force && sessionEmployeeInFlight) {
    return sessionEmployeeInFlight;
  }

  sessionEmployeeInFlight = resolveSessionEmployeeRemote(uid).finally(() => {
    sessionEmployeeInFlight = null;
  });
  return sessionEmployeeInFlight;
}

async function resolveSessionEmployeeRemote(userId: string): Promise<SessionEmployeeRecord> {
  let fromOffline: SessionEmployeeRecord | null = null;
  try {
    const snap = await getOfflineAccountSnapshot(userId);
    if (snap && typeof snap.isEmployee === 'boolean') {
      fromOffline = {
        employeeId: null,
        isEmployee: snap.isEmployee,
        isActive: true,
      };
    }
  } catch {
    /* ignore */
  }

  recordStartupNetworkCall('employees', 'employees?id,is_active,organization_id,permissions_updated_at,role');
  // Multi-org: N filas (activas e inactivas); preferimos org activa + activa.
  const { data: rows, error } = await supabase
    .from('employees')
    .select('id, is_active, organization_id, permissions_updated_at, role')
    .eq('auth_user_id', userId)
    .limit(10);

  if (error) {
    if (!isDatabaseUnavailableError(error)) {
      console.warn('[sessionEmployeeResolver]', error.message);
    }
    if (fromOffline) {
      return cacheAndReturn(userId, fromOffline);
    }
    return cacheAndReturn(userId, { employeeId: null, isEmployee: false, isActive: true });
  }

  // role='owner' = membresía del dueño (identidad para agenda/atribución),
  // no empleo — no debe flipar isEmployee ni la semántica de sesión.
  const list = (Array.isArray(rows) ? rows : []).filter(isSessionEmployeeRow);
  let data = list[0] ?? null;
  try {
    const { getSessionOrganizationId } = await import('../../shell/session/sessionOrganization');
    const orgId = await getSessionOrganizationId();
    if (orgId) {
      const orgRows = list.filter((r) => r.organization_id === orgId);
      const preferred =
        orgRows.find((r) => r.is_active !== false) ?? orgRows[0] ?? null;
      if (preferred) data = preferred;
    } else {
      data = list.find((r) => r.is_active !== false) ?? list[0] ?? null;
    }
  } catch {
    data = list.find((r) => r.is_active !== false) ?? list[0] ?? null;
  }

  const record: SessionEmployeeRecord = data
    ? {
        employeeId: data.id as string,
        isEmployee: true,
        isActive: data.is_active !== false,
        permissionsUpdatedAt:
          typeof (data as { permissions_updated_at?: unknown }).permissions_updated_at === 'string'
            ? ((data as { permissions_updated_at: string }).permissions_updated_at)
            : null,
      }
    : { employeeId: null, isEmployee: false, isActive: true, permissionsUpdatedAt: null };

  try {
    await saveIsEmployeeOffline(userId, record.isEmployee);
  } catch (e) {
    if (!isDatabaseUnavailableError(e)) {
      console.warn('[sessionEmployeeResolver] cache write', e);
    }
  }

  return cacheAndReturn(userId, record);
}

function cacheAndReturn(userId: string, value: SessionEmployeeRecord): SessionEmployeeRecord {
  sessionEmployeeCache = { value, fetchedAt: Date.now(), userId };
  return value;
}
